import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, AlertTriangle, Check } from 'lucide-react';

const ContentGuidelines = () => {
  return (
    <div className="min-h-screen bg-black text-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        <Link to="/" className="text-orange-500 hover:text-orange-600 mb-8 inline-block">
          ← Back to Home
        </Link>
        
        <h1 className="text-4xl font-bold mb-4">Content Guidelines</h1>
        <p className="text-gray-400 mb-8">Community standards for creators and readers</p>
        
        <div className="space-y-8 text-gray-300">
          <section className="bg-gradient-to-r from-orange-500/10 to-red-500/10 border border-orange-500/20 rounded-lg p-6">
            <div className="flex items-start gap-3">
              <Shield className="w-6 h-6 text-orange-500 flex-shrink-0 mt-1" />
              <div>
                <h2 className="text-xl font-bold text-white mb-2">Our Mission</h2>
                <p>
                  Trap Comix is a platform for underrepresented creators to share their stories, art, and culture. 
                  We support creative freedom while maintaining a safe and respectful community.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
              <Check className="w-6 h-6 text-green-500" />
              What's Allowed
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
                <h3 className="font-semibold text-orange-500 mb-2">📚 Original Comics & Novels</h3>
                <p className="text-sm">Create and share your original webcomics, manga, graphic novels, and written stories.</p>
              </div>
              <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
                <h3 className="font-semibold text-orange-500 mb-2">🎨 Artwork & Illustrations</h3>
                <p className="text-sm">Upload character designs, fan art, digital paintings, and concept art.</p>
              </div>
              <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
                <h3 className="font-semibold text-orange-500 mb-2">🎵 Music & Audio</h3>
                <p className="text-sm">Share original music, beats, soundtracks, and audio projects.</p>
              </div>
              <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
                <h3 className="font-semibold text-orange-500 mb-2">🗣️ Commentary & Critique</h3>
                <p className="text-sm">Thoughtful discussions, reviews, and constructive feedback on content.</p>
              </div>
              <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
                <h3 className="font-semibold text-orange-500 mb-2">🔥 Mature Content (Tagged)</h3>
                <p className="text-sm">Violence, profanity, and sexual themes are allowed if properly tagged and age-gated.</p>
              </div>
              <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
                <h3 className="font-semibold text-orange-500 mb-2">💎 Fan Art & Tributes</h3>
                <p className="text-sm">Fan works are allowed as long as they don't infringe copyright or pass off as official.</p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
              <AlertTriangle className="w-6 h-6 text-red-500" />
              What's Prohibited
            </h2>
            <div className="space-y-4">
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                <h3 className="font-semibold text-red-400 mb-2">🚫 Illegal Content</h3>
                <ul className="text-sm list-disc list-inside space-y-1">
                  <li>Child sexual abuse material (CSAM) - zero tolerance, reported to authorities</li>
                  <li>Content promoting terrorism or violence</li>
                  <li>Instructions for illegal activities (bombs, drugs, hacking)</li>
                  <li>Pirated or stolen copyrighted material</li>
                </ul>
              </div>

              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                <h3 className="font-semibold text-red-400 mb-2">🚫 Hate Speech & Harassment</h3>
                <ul className="text-sm list-disc list-inside space-y-1">
                  <li>Content targeting people based on race, ethnicity, religion, gender, sexual orientation, or disability</li>
                  <li>Bullying, doxxing, or stalking</li>
                  <li>Threats of violence or harm</li>
                  <li>Coordinated harassment campaigns</li>
                </ul>
              </div>

              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                <h3 className="font-semibold text-red-400 mb-2">🚫 Spam & Manipulation</h3>
                <ul className="text-sm list-disc list-inside space-y-1">
                  <li>Fake engagement (buying views, likes, followers)</li>
                  <li>Misleading titles or thumbnails (clickbait)</li>
                  <li>Excessive self-promotion or unsolicited advertising</li>
                  <li>Phishing, scams, or malware</li>
                </ul>
              </div>

              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                <h3 className="font-semibold text-red-400 mb-2">🚫 Sexual Content Violations</h3>
                <ul className="text-sm list-disc list-inside space-y-1">
                  <li>Any sexual content involving minors (even fictional)</li>
                  <li>Non-consensual sexual content (revenge porn, deepfakes)</li>
                  <li>Sexually explicit content without proper age gates</li>
                  <li>Prostitution or escort services</li>
                </ul>
              </div>

              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                <h3 className="font-semibold text-red-400 mb-2">🚫 Copyright & Impersonation</h3>
                <ul className="text-sm list-disc list-inside space-y-1">
                  <li>Uploading others' work without permission</li>
                  <li>Impersonating other users or celebrities</li>
                  <li>Using copyrighted material without fair use justification</li>
                  <li>Trademark infringement</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">Content Tagging Requirements</h2>
            <p className="mb-3">Creators <strong className="text-orange-500">must</strong> properly tag content containing:</p>
            <ul className="list-disc list-inside space-y-2">
              <li><strong>Violence/Gore:</strong> Graphic violence, blood, or disturbing imagery</li>
              <li><strong>Sexual Content:</strong> Nudity, sexual situations, or suggestive themes</li>
              <li><strong>Profanity:</strong> Strong language or explicit dialogue</li>
              <li><strong>Substance Use:</strong> Drug use, alcohol, or smoking</li>
              <li><strong>Sensitive Topics:</strong> Suicide, self-harm, mental health crises</li>
            </ul>
            <p className="mt-3 text-yellow-500">
              ⚠️ Failure to properly tag mature content may result in content removal or account suspension.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">Enforcement & Appeals</h2>
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-orange-500 mb-2">1. Warning (First Offense)</h3>
                <p className="text-sm">Minor violations receive a warning and content removal.</p>
              </div>
              <div>
                <h3 className="font-semibold text-orange-500 mb-2">2. Temporary Suspension (Repeat Offenses)</h3>
                <p className="text-sm">7-30 day suspension for repeated or moderate violations.</p>
              </div>
              <div>
                <h3 className="font-semibold text-orange-500 mb-2">3. Permanent Ban (Serious Violations)</h3>
                <p className="text-sm">Immediate ban for illegal content, CSAM, severe harassment, or repeat offenders.</p>
              </div>
            </div>
            <p className="mt-4">
              To appeal a moderation decision, email <a href="mailto:appeals@trapcomix.com" className="text-orange-500 hover:text-orange-600">appeals@trapcomix.com</a> with:
            </p>
            <ul className="list-disc list-inside mt-2 space-y-1 text-sm">
              <li>Your username and email</li>
              <li>The content or account in question</li>
              <li>Why you believe the decision was incorrect</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">Reporting Violations</h2>
            <p className="mb-3">
              If you see content that violates these guidelines, please report it:
            </p>
            <ul className="list-disc list-inside space-y-2">
              <li>Click the "Report" button on the content</li>
              <li>Select the violation type</li>
              <li>Provide additional context if needed</li>
            </ul>
            <p className="mt-3 text-gray-400 text-sm">
              All reports are reviewed by our moderation team. False reports may result in account penalties.
            </p>
          </section>

          <section className="bg-gray-900 border border-gray-800 rounded-lg p-6">
            <h2 className="text-xl font-bold text-white mb-3">Questions?</h2>
            <p>
              Contact our moderation team: <br />
              <a href="mailto:moderation@trapcomix.com" className="text-orange-500 hover:text-orange-600">
                moderation@trapcomix.com
              </a>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default ContentGuidelines;